import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'
import { m } from 'framer-motion'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import ButtonText from '@segment/matcha/components/ButtonText'
import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import PersonalizeEveryInteractionImage from './PersonalizeEveryInteractionImage'

const copy = {
  heading: `Personalize every interaction across the omnichannel customer journey`,
}

const link = {
  text: `Explore Twilio Engage`,
  url: `/product/twilio-engage/`,
}

const list = [
  {
    title: `Build`,
    description: `Create real-time audiences and campaigns without relying on your engineering team.`,
  },
  {
    title: `Engage`,
    description: `Deliver personalized experiences wherever your customers are, whether in a local branch or online.`,
  },
  {
    title: `Retain`,
    description: `Use individualized data to drive the behaviors or product features that increase customer loyalty and engagement.`,
  },
]

const PersonalizeEveryInteraction: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  return (
    <SectionBase variant="2Col" copyVariant="narrow" sticky={true}>
      <Section.Copy>
        <CopyTextBase>
          <CopyText.Heading tag="h2">{copy.heading}</CopyText.Heading>
        </CopyTextBase>
        <ColumnTrackWrapper>
          <ColumnTrack gap="medium">
            {list.map(({ title, description }) => (
              <FeatureBoxBase alignment="left" key={title}>
                <FeatureBox.Body>
                  <FeatureBox.Title tag="h3" variant="h5">
                    {title}
                  </FeatureBox.Title>
                  <FeatureBox.Text>{description}</FeatureBox.Text>
                </FeatureBox.Body>
              </FeatureBoxBase>
            ))}
            <ButtonWrapper>
              <ButtonText
                tag="a"
                href={getAbsoluteUrl(link.url, pageConfig)}
                target="_blank"
                iconEnd={ChevronRightIcon}
              >
                {link.text}
              </ButtonText>
            </ButtonWrapper>
          </ColumnTrack>
        </ColumnTrackWrapper>
      </Section.Copy>
      <Section.Contents>
        <Section.Sticky>
          <ImageWrapper
            initial={{ opacity: 0 }}
            whileInView={{
              opacity: 1,
              transition: {
                duration: 0.4,
                delay: 0.1,
              },
            }}
            viewport={{ once: true }}
          >
            <PersonalizeEveryInteractionImage />
          </ImageWrapper>
        </Section.Sticky>
      </Section.Contents>
    </SectionBase>
  )
}

export default PersonalizeEveryInteraction

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

const ButtonWrapper = styled.div``

const ImageWrapper = styled(m.div)``
